<template>
  <div class="accounting-tab">
    <v-layout justify-space-between mb-3>
      <h2>Filtrer</h2>
      <v-spacer></v-spacer>
      <v-btn class="mr-6" :loading="isPrintLoading" @click="handlePrint"
        >Imprimer l'édition comptable</v-btn
      >
      <v-btn @click="handleExcelExport" :loading="isExcelExportLoading"
        >Export excel</v-btn
      >
    </v-layout>

    <!-- Filter block -->
    <v-layout class="filters-container" mb-8 wrap>
      <v-flex sm12>
        <v-layout justify-space-between>
          <v-flex sm4>
            <v-dialog v-model="dialogDatepickerFrom" width="290px">
              <template #activator="{ on }">
                <v-text-field
                  label="Du"
                  append-icon="mdi-calendar"
                  :value="filters.from | dateFormat"
                  v-on="on"
                  @click:clear="filters.from = null"
                  clearable
                  single-line
                ></v-text-field>
              </template>
              <v-date-picker
                first-day-of-week="1"
                v-model="filters.from"
                @change="dialogDatepickerFrom = false"
              >
              </v-date-picker>
            </v-dialog>
          </v-flex>
          <v-flex sm4>
            <v-dialog v-model="dialogDatepickerTo" width="290px">
              <template #activator="{ on }">
                <v-text-field
                  label="Au"
                  append-icon="mdi-calendar"
                  :value="filters.to | dateFormat"
                  v-on="on"
                  @click:clear="filters.to = null"
                  single-line
                  clearable
                ></v-text-field>
              </template>
              <v-date-picker
                first-day-of-week="1"
                v-model="filters.to"
                @change="dialogDatepickerTo = false"
              >
              </v-date-picker>
            </v-dialog>
          </v-flex>
          <v-flex sm4>
            <v-btn class="mt-4" block @click="fetchAccountingData"
              >Rechercher</v-btn
            >
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex sm4>
            <v-select
              label="Type de date"
              v-model="dateType"
              :items="dateTypes"
              filled
              hide-details
            ></v-select>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <v-data-table
      :loading="isLoading"
      loading-text="Chargement des données"
      group-by="applicator_fullname"
      height="500"
      :headers="headers"
      :options.sync="pagination"
      :server-items-length="totalServerItems"
      :items="filteredProjects"
      :items-per-page="100"
      :footer-props="{
        itemsPerPageOptions: [100, 500, 1000],
      }"
      fixed-header
    >
      <!-- Date -->
      <template #item.created_at="{ value }">
        {{ value | dateFormat }}
      </template>

      <!-- Montant devis -->
      <template #item.total_amount_ht_lomaco_quotation="{ value }">
        {{ value | currency }}
      </template>

      <template #group.header="{ group, items }">
        <td class="text-start" colspan="6">{{ group }}</td>
        <td class="text-start">
          Total Devis : {{ calculateTotalQuotation(items) | currency }}
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import PrintRepo from "@/repositories/print";
import ProjectsRepo from "@/repositories/projects";

export default {
  name: "AccountingTab",
  data() {
    return {
      isLoading: false,
      isPrintLoading: false,
      isExcelExportLoading: false,

      headers: [
        {
          text: "N° Dossier",
          align: "left",
          value: "file_number",
        },

        {
          text: "Type",
          align: "left",
          value: "device_label",
        },

        {
          text: "Nom du patient",
          align: "left",
          value: "patient_fullname",
        },

        {
          text: "Centre",
          align: "left",
          value: "center",
        },

        {
          text: "Date",
          align: "left",
          value: "created_at",
        },

        {
          text: "N° Devis",
          align: "left",
          value: "quotation_number",
        },

        {
          text: "Montant Devis",
          align: "left",
          value: "total_amount_ht_lomaco_quotation",
        },
      ],

      projects: [],
      totalServerItems: 0,
      pagination: {},
      filters: {
        from: null,
        to: null,
      },

      dialogDatepickerFrom: false,
      dialogDatepickerTo: false,

      dateType: "MOLDING_AND_QUOTATION",
      dateTypes: [
        {
          value: "MOLDING_AND_QUOTATION",
          text: "Moulages et devis par applicateurs creation",
        },
      ],
    };
  },

  computed: {
    filteredProjects() {
      return this.projects.map((item) => {
        item.applicator_fullname = `${item.lastname.toUpperCase()} ${
          item.firstname
        }`;
        item.patient_fullname = `${item.patient.lastname} ${item.patient.firstname}`;
        return item;
      });
    },
  },

  watch: {
    pagination: {
      handler() {
        this.fetchAccountingData();
      },
      deep: true,
    },
  },

  methods: {
    async fetchAccountingData() {
      try {
        this.isLoading = true;

        // Handle query params
        const { from, to } = this.filters;
        const { page, itemsPerPage } = this.pagination;
        const params = { from, to, page, itemsPerPage };

        const { data, total } = await ProjectsRepo.fetchAccountingProjects(
          params
        );

        this.projects = data;
        this.totalServerItems = total;
      } catch (error) {
        console.error(error);
        this.$notify({
          text: "Une erreur est survenue lors de la récupération des données",
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async handleExcelExport() {
      try {
        this.isExcelExportLoading = true;

        // Handle query params
        const params = {
          date_start: this.filters.from || null,
          date_end: this.filters.to || new Date().toISOString().split("T")[0],
        };

        const data = await PrintRepo.getAccountingExcel(params);

        // Create file based on api response
        let blob = new Blob([data], {
          type: "application/vnd.ms-excel",
        });

        // Create a link element to trigger download
        let link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        // Generate filename
        const date = new Date().toISOString().split("T")[0];
        const hour = new Date().toISOString().split("T")[1];
        const filename = `${date}-${hour}-compta.xlsx`;
        link.download = filename;

        // Donwload file
        link.click();
      } catch (error) {
        console.error(error);
        this.$notify({
          type: "error",
          text: "Une erreur est survenue lors du téléchargement",
        });
      } finally {
        this.isExcelExportLoading = false;
      }
    },

    /**
     * Sum prop value in an array of items
     *
     *  @param {Array} items
     */
    calculateTotalQuotation(items) {
      return items.reduce(
        (a, b) => a + (b.total_amount_ht_lomaco_quotation || 0),
        0
      );
    },

    async handlePrint() {
      try {
        this.isPrintLoading = true;

        // Handles filters
        const params = {
          date_start: this.filters.from || null,
          date_end: this.filters.to || new Date().toISOString().split("T")[0],
        };

        const { data } = await PrintRepo.getAccountingPdf(params);

        // Create file based on api response
        let blob = new Blob([data], {
          type: "application/pdf",
        });

        // Create a link element to trigger download
        let link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        // Generate filename
        const date = new Date().toISOString().split("T")[0];
        const hour = new Date().toISOString().split("T")[1];
        const filename = `${date}-${hour}-compta.pdf`;
        link.download = filename;

        // Donwload file
        link.click();
      } catch (error) {
        console.error(error);
        this.$notify({
          type: "error",
          text: "Une erreur est survenue lors du téléchargement",
        });
      } finally {
        this.isPrintLoading = false;
      }
    },
  },
};
</script>

<style></style>
